import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  PasswordInput,
  ScrollArea,
  TextInput,
  Stack,
  MultiSelect,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { CREATE_STAFF } from "apollo/mutuations"; // Make sure this mutation accepts business_ids: [ID!]
import { GET_MY_SUPPLIERS_Business, GET_STAFFS } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";

export const SalesAddModal = ({
  setOpened,
  size,
  activePage,
  refetchBusinesses,
}) => {
  const [businessDropDownData, setBusinessDropDownData] = useState([]);

  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      business_ids: [],
    },

    validate: {
      name: (value) => (value.trim().length < 2 ? "Name is too short" : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      phone: (value) =>
        value.length !== 13
          ? "Phone number must be exactly 13 characters (e.g. +251912345678)"
          : null,
      password: (value) =>
        value.length < 6 ? "Password should be at least 6 characters" : null,
      business_ids: (value) =>
        value.length === 0 ? "Please select at least one business" : null,
    },
  });

  const { loading: businessLoading, refetch } = useQuery(
    GET_MY_SUPPLIERS_Business,
    {
      variables: {
        first: 100,
        page: 1,
        ordered_by: [
          {
            column: "CREATED_AT",
            order: "DESC",
          },
        ],
      },
      onCompleted: (data) => {
        const businesses =
          data?.myBusinesses?.data?.map((business) => ({
            label: business.business_name,
            value: business.id,
          })) || [];

        setBusinessDropDownData(businesses);
      },
      onError: (error) => {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to load businesses",
        });
      },
    }
  );

  useEffect(() => {
    refetch();
  }, []);

  const { height } = useViewportSize();

  const [addSales, { loading: salesLoading }] = useMutation(CREATE_STAFF, {
    update(cache, { data: { createStaff } }) {
      cache.updateQuery(
        {
          query: GET_STAFFS,
          variables: {
            first: parseInt(10),
            page: 1,
            search: "",
          },
        },
        (data) => {
          if (data) {
            return {
              staffs: {
                ...data.staffs,
                data: [createStaff, ...data.staffs.data],
              },
            };
          }
        }
      );
    },
  });

  const handleSubmit = (values) => {
    addSales({
      variables: {
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        password: values.password,
        business_ids: values.business_ids, // ← array of ids!
      },
      onCompleted: () => {
        refetchBusinesses();
        showNotification({
          color: "green",
          title: "Success",
          message: "Staff member created successfully",
        });
        form.reset();
        setOpened(false);
      },
      onError: (error) => {
        console.error(error);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to create staff",
        });
      },
    });
  };

  return (
    <>
      <LoadingOverlay
        visible={salesLoading || businessLoading}
        overlayBlur={2}
        loader={customLoader}
      />

      <ScrollArea
        style={{ height: height / 1.8, paddingRight: 12 }}
        type="auto"
        offsetScrollbars
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack spacing="lg">
            <Grid gutter="xl">
              <Grid.Col span={4}>
                <TextInput
                  required
                  label="Full Name"
                  placeholder="Enter full name"
                  {...form.getInputProps("name")}
                />

                <TextInput
                  required
                  label="Email"
                  placeholder="example@company.com"
                  {...form.getInputProps("email")}
                />

                <TextInput
                  required
                  label="Phone Number"
                  placeholder="+251912345678"
                  maxLength={13}
                  {...form.getInputProps("phone")}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9+]/g, "");
                  }}
                />
              </Grid.Col>

              <Grid.Col span={4}>
                <MultiSelect
                  required
                  label="Business"
                  placeholder="Select one or more businesses"
                  searchable
                  nothingFound="No businesses found"
                  maxDropdownHeight={280}
                  data={businessDropDownData}
                  value={form.values.business_ids}
                  onChange={(values) =>
                    form.setFieldValue("business_ids", values)
                  }
                  error={form.errors.business_ids}
                />
                <PasswordInput
                  required
                  label="Password"
                  {...form.getInputProps("password")}
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <Button
                  style={{
                    width: "25%",
                    marginTop: "15px",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  type="submit"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};
